/**
 * Purchasable Question Set Registry
 *
 * Single source of truth for the life-stage question packs: pricing, copy,
 * and display metadata. Consumed by QuestionSetPromoCard, the premium badge
 * in IndividualWork, and the re-exports in stripe.ts.
 *
 * INVARIANT: `id` is used as BOTH the Stripe productId AND the Firestore
 * `question_set` value. There is no mapping table anywhere — the webhook
 * writes the raw Stripe productId into `userProfiles.purchasedSets`, and the
 * client matches that string against `question_set` verbatim. If the two ever
 * diverge, the pack is purchasable but its questions never unlock (this
 * shipped as 'valentine-2026' vs 'valentines' and was fixed by unifying on
 * 'valentines').
 *
 * The amounts here are display-only. The server sets the real charge from
 * PRODUCT_PRICES in functions/src/index.ts — keep the two in sync.
 */

import { Heart, Home, Baby, Flame, Gem, type LucideIcon } from 'lucide-react';

export interface QuestionSetDef {
  /** Stripe productId AND Firestore question_set. Must match both. */
  id: string;
  name: string;
  description: string;
  /** Display price in cents. Server is authoritative — see functions/src/index.ts */
  amount: number;
  currency: string;
  questionCount: number;
  features: string[];
  icon: LucideIcon;
  /** Highest comfort_level among the pack's questions (1-3). Used to warn a buyer
   *  when the relationship's comfort level is too low to use the deck. */
  maxComfortLevel?: number;
}

export const QUESTION_SETS: Record<string, QuestionSetDef> = {
  'stage-dating': {
    id: 'stage-dating',
    name: 'Dating & Early Relationship',
    description: '15 questions on the things new couples postpone rather than settle',
    amount: 1499,
    currency: 'usd',
    questionCount: 15,
    features: [
      'Commitment pace and where this is going',
      'Time, autonomy and the trust perimeter',
      'Deal-breakers named before they surface',
      'Includes partner-prediction questions',
    ],
    icon: Heart,
  },
  'stage-cohabiting': {
    id: 'stage-cohabiting',
    name: 'Moving In Together',
    description: '18 questions on the household precedents that tend to stick',
    amount: 1799,
    currency: 'usd',
    questionCount: 18,
    features: [
      'Household labour split and cleaning standards',
      'How money is held and what needs discussing',
      'In-law boundaries and holiday allocation',
      'Whose career wins when they collide',
    ],
    icon: Home,
  },
  'stage-parenthood': {
    id: 'stage-parenthood',
    name: 'The First Five Years of Parenthood',
    description: '25 questions for the highest-conflict phase of raising young children',
    amount: 2499,
    currency: 'usd',
    questionCount: 25,
    features: [
      'Invisible load and night-waking allocation',
      'Sleep, desire and getting time back',
      'Grandparent boundaries and parenting methods',
      'Protecting the couple, not just the child',
    ],
    icon: Baby,
  },
  'sexuality-desires': {
    id: 'sexuality-desires',
    name: 'Sexuality & Desires',
    description: '34 questions on sexual preferences, desires, and connection',
    amount: 3399,
    currency: 'usd',
    questionCount: 34,
    maxComfortLevel: 3,
    features: [
      'Desire styles and what unlocks intimacy',
      'Preferences, boundaries, and consent language',
      'Shared agreements for exploring together',
      'Keeping desire alive across life changes',
    ],
    icon: Flame,
  },
  'pre-marriage': {
    id: 'pre-marriage',
    name: 'Pre-Marriage Preparation',
    description: '24 curated questions to prepare for your marriage journey',
    amount: 2399,
    currency: 'usd',
    questionCount: 24,
    features: [
      '12 individual reflection questions',
      '12 joint collaboration questions',
      'Covers all 6 core relationship topics',
      'Integrated into your Love Contract',
    ],
    icon: Gem,
  },
};

/** All packs, in the order they should be offered. */
export const allQuestionSets: QuestionSetDef[] = [
  QUESTION_SETS['stage-dating'],
  QUESTION_SETS['stage-cohabiting'],
  QUESTION_SETS['stage-parenthood'],
  QUESTION_SETS['sexuality-desires'],
  QUESTION_SETS['pre-marriage'],
];

/** Look up a pack by its questionSet / productId. Returns undefined for unknown or legacy sets. */
export function getQuestionSet(id?: string): QuestionSetDef | undefined {
  return id ? QUESTION_SETS[id] : undefined;
}
